import React, { useState, useEffect, useRef } from 'react';
import { STRRoom, STRBooking } from './types';
import { formatNumber, getChannelBg, channelBorderColors } from './utils';
import { API_BASE_URL } from '../config';
import { useResponsive } from '../hooks/useResponsive';

interface BookingMemo {
  id: number;
  content: string;
  author_name: string;
  created_at: string;
}

interface Props {
  room: STRRoom;
  token: string;
  onRoomUpdated: (room: STRRoom) => void;
  onEditBooking: (booking: STRBooking) => void;
  onCancelBooking: (booking: STRBooking) => void;
  onEditBlock: (index: number) => void;
  onDeleteBlock: (index: number) => void;
}

const PER_PAGE = 10;

export default function BookingList({ room, token, onRoomUpdated, onEditBooking, onCancelBooking, onEditBlock, onDeleteBlock }: Props) {
  const { isMobile } = useResponsive();
  const [bookingPage, setBookingPage] = useState(1);
  const [blockedPage, setBlockedPage] = useState(1);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Memo modal state
  const [memoBooking, setMemoBooking] = useState<STRBooking | null>(null);
  const [memos, setMemos] = useState<BookingMemo[]>([]);
  const [memoInput, setMemoInput] = useState('');
  const [memoLoading, setMemoLoading] = useState(false);
  const memoInputRef = useRef<HTMLTextAreaElement>(null);
  const [labelTooltip, setLabelTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

  const openMemoModal = async (b: STRBooking) => {
    setMemoBooking(b);
    setMemos([]);
    setMemoInput('');
    const res = await fetch(`${API_BASE_URL}/api/str-rooms/${room.id}/bookings/${b.id}/memos`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      setMemos(data.memos || []);
    }
    setTimeout(() => memoInputRef.current?.focus(), 100);
  };

  const submitMemo = async () => {
    if (!memoBooking || !memoInput.trim()) return;
    setMemoLoading(true);
    const res = await fetch(`${API_BASE_URL}/api/str-rooms/${room.id}/bookings/${memoBooking.id}/memos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ content: memoInput.trim() }),
    });
    if (res.ok) {
      const newMemo = await res.json();
      setMemos((prev) => [...prev, newMemo]);
      setMemoInput('');
    }
    setMemoLoading(false);
  };

  const deleteMemo = async (memoId: number) => {
    if (!memoBooking) return;
    const res = await fetch(`${API_BASE_URL}/api/str-rooms/${room.id}/bookings/${memoBooking.id}/memos/${memoId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      setMemos((prev) => prev.filter((m) => m.id !== memoId));
    }
  };

  const allBookings = [...(room.bookings || [])].sort((a, b) => (a.check_in < b.check_in ? 1 : -1));
  const allBlocked = room.blocked_dates || [];
  const hasBookings = allBookings.length > 0;
  const hasBlocked = allBlocked.length > 0;

  if (!hasBookings && !hasBlocked) return null;

  const totalBkPages = Math.ceil(allBookings.length / PER_PAGE);
  const bkStart = (bookingPage - 1) * PER_PAGE;
  const bkSlice = allBookings.slice(bkStart, bkStart + PER_PAGE);
  const activeForFirst = allBookings.filter((b) => b.status !== 'cancelled');
  const firstBookingId = activeForFirst.length > 0 ? activeForFirst[activeForFirst.length - 1].id : null;

  const totalBlPages = Math.ceil(allBlocked.length / PER_PAGE);
  const blStart = (blockedPage - 1) * PER_PAGE;
  const blSlice = allBlocked.slice(blStart, blStart + PER_PAGE);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleMsgSent = async (bookingId: string, key: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/str-rooms/${room.id}/bookings/${bookingId}/msg-sent`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ key }),
      });
      if (res.ok) {
        const data = await res.json();
        const updatedBookings = room.bookings.map((b) =>
          b.id === bookingId ? { ...b, msg_sent: data.msg_sent } : b
        );
        onRoomUpdated({ ...room, bookings: updatedBookings });
      }
    } catch (e) { console.error(e); }
  };

  const togglePaymentStatus = async (bookingId: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/str-rooms/${room.id}/bookings/${bookingId}/payment-status`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const updatedBookings = room.bookings.map((b) =>
          b.id === bookingId ? { ...b, payment_status: data.payment_status } : b
        );
        onRoomUpdated({ ...room, bookings: updatedBookings });
      }
    } catch (e) { console.error(e); }
  };

  const toggleDepositRefund = async (bookingId: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/str-rooms/${room.id}/bookings/${bookingId}/deposit-refund-status`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const updatedBookings = room.bookings.map((b) =>
          b.id === bookingId ? { ...b, deposit_refund_status: data.deposit_refund_status } : b
        );
        onRoomUpdated({ ...room, bookings: updatedBookings });
      }
    } catch (e) { console.error(e); }
  };

  const msgKeys = ['msg_contract', 'msg_checkin', 'msg_checkout_prev', 'msg_checkout_day'] as const;
  const msgShort = ['계약', '입실', '퇴실전', '퇴실'];

  // Label shortening
  const LABEL_SHORT: Record<string, string> = {
    '직거래 연장': '직거래', '리브애니웨어': '리브', '에어비앤비': '에어비', '엔코스테이': '엔코',
  };
  const shortLabel = (name: string) => LABEL_SHORT[name] || name;
  const showLabelTooltip = (e: React.MouseEvent, text: string) => {
    e.stopPropagation();
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setLabelTooltip({ text, x: r.left, y: r.bottom + 4 });
  };

  // c1 button base style
  const btnSm = (borderColor: string, textColor: string): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '4px 10px', fontSize: 11, fontWeight: 500,
    border: 'none', borderRadius: 8, cursor: 'pointer',
    background: '#f5f5f5', color: textColor,
    borderStyle: 'solid', borderWidth: 1, borderColor,
  });

  return (
    <div style={{ marginTop: 16 }}>
      {/* Label tooltip */}
      {labelTooltip && (
        <div onClick={() => setLabelTooltip(null)} style={{ position: 'fixed', inset: 0, zIndex: 1500 }}>
          <div style={{ position: 'fixed', left: labelTooltip.x, top: labelTooltip.y, background: '#1e293b', color: '#fff', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 500, pointerEvents: 'none', whiteSpace: 'nowrap' }}>
            {labelTooltip.text}
          </div>
        </div>
      )}
      {/* Memo modal */}
      {memoBooking && (
        <div
          onClick={() => setMemoBooking(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 480, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
          >
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>메모</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{memoBooking.check_in} ~ {memoBooking.check_out} · {memoBooking.guest_name}</div>
              </div>
              <button onClick={() => setMemoBooking(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#64748b', padding: 0, lineHeight: 1 }}>✕</button>
            </div>
            {/* Memo list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {memos.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: '20px 0' }}>메모가 없습니다.</div>
              ) : (
                memos.map((m) => (
                  <div key={m.id} style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 12px', position: 'relative' }}>
                    <div style={{ fontSize: 13, color: '#1e293b', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{m.content}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>
                        {m.author_name && `${m.author_name} · `}
                        {m.created_at ? new Date(m.created_at).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                      <button onClick={() => deleteMemo(m.id)} style={{ background: 'none', border: 'none', fontSize: 11, color: '#cbd5e1', cursor: 'pointer', padding: 0 }}>삭제</button>
                    </div>
                  </div>
                ))
              )}
            </div>
            {/* Input */}
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 8 }}>
              <textarea
                ref={memoInputRef}
                value={memoInput}
                onChange={(e) => setMemoInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submitMemo(); }}
                placeholder="메모 입력... (Ctrl+Enter로 저장)"
                rows={2}
                style={{ flex: 1, resize: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
              />
              <button
                onClick={submitMemo}
                disabled={memoLoading || !memoInput.trim()}
                style={{ padding: '0 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!memoInput.trim() || memoLoading) ? 0.5 : 1 }}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}
      {hasBookings && (
        <>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
            예약/정산 현황 <span style={{ fontSize: 12, color: '#888', fontWeight: 400 }}>{allBookings.length}건</span>
          </h3>
          {bkSlice.map((b) => {
            const isCancelled = b.status === 'cancelled';
            const displayPrice = b.final_price || b.total_price;
            const dailyPrice = b.days > 0
              ? Math.round((b.negotiated_price ? b.negotiated_price : ((b.total_rent || 0) + (b.total_maintenance || 0) + (b.total_cleaning || 0))) / b.days)
              : 0;
            const showCleanExp = !isCancelled && b.id !== firstBookingId && b.booking_type !== '직거래 연장' && (b.cleaning_expense || 0) > 0;
            const isPaid = b.payment_status === 'completed';
            const isRefunded = b.deposit_refund_status === 'completed';
            const ms = b.msg_sent || {};

            return (
              <div key={b.id}>
                {/* Booking row */}
                <div
                  onClick={() => toggleExpand(b.id)}
                  style={isMobile ? {
                    padding: '10px 12px', background: '#f5f5f5', borderRadius: 8,
                    cursor: 'pointer', marginBottom: 6,
                    opacity: isCancelled ? 0.5 : 1,
                    textDecoration: isCancelled ? 'line-through' : 'none',
                  } : {
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 12px', background: '#f5f5f5', borderRadius: 6,
                    fontSize: 13, cursor: 'pointer', marginBottom: 4,
                    opacity: isCancelled ? 0.5 : 1,
                    textDecoration: isCancelled ? 'line-through' : 'none',
                  }}
                >
                  {isMobile ? (
                    <>
                      {/* 모바일 1행: 날짜 + 채널 */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', fontSize: 13 }}>
                        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{b.check_in} ~ {b.check_out}</span>
                        {b.booking_type === '직거래 연장' && (
                          <span onClick={(e) => showLabelTooltip(e, '직거래 연장')} style={{ padding: '1px 5px', background: '#e8f5e9', color: '#2e7d32', border: '1px solid #2e7d32', borderRadius: 4, fontSize: 9, cursor: 'pointer' }}>직거래</span>
                        )}
                        <span
                          onClick={(e) => LABEL_SHORT[b.channel] ? showLabelTooltip(e, b.channel) : e.stopPropagation()}
                          style={{
                            padding: '1px 5px', borderRadius: 4, fontSize: 9,
                            background: getChannelBg(b.channel),
                            color: channelBorderColors[b.channel] || '#1976d2',
                            border: `1px solid ${channelBorderColors[b.channel] || '#90caf9'}`,
                            cursor: LABEL_SHORT[b.channel] ? 'pointer' : 'default',
                          }}>
                          {shortLabel(b.channel)}
                        </span>
                      </div>
                      {/* 모바일 2행: 알림 발송 토글 */}
                      {!isCancelled && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 4 }}>
                          <span style={{ fontSize: 9, color: '#1976d2' }}>알림</span>
                          {msgKeys.map((mk, mi) => {
                            const sent = !!ms[mk];
                            return (
                              <span
                                key={mk}
                                onClick={(e) => { e.stopPropagation(); toggleMsgSent(b.id, mk); }}
                                style={{
                                  fontSize: 9, padding: '1px 4px', borderRadius: 3, cursor: 'pointer',
                                  lineHeight: '1.3',
                                  border: `1px solid ${sent ? '#4caf50' : '#ccc'}`,
                                  color: sent ? '#4caf50' : '#bbb', background: 'transparent',
                                }}
                              >
                                {sent ? '✓' : ''}{msgShort[mi]}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {/* 모바일 3행: 게스트 · 일수 + (청소비) + 가격 */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 5 }}>
                        <span style={{ fontSize: 12, color: '#888' }}>{b.guest_name || ''} · {b.days}일</span>
                        <span style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                          {showCleanExp && <span style={{ fontSize: 10, color: '#e65100' }}>청소 {formatNumber(b.cleaning_expense)}</span>}
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#1976d2' }}>
                            {formatNumber(displayPrice)}원
                            {b.negotiated_price > 0 && <span style={{ fontSize: 9, color: '#e65100', marginLeft: 3 }}>협의</span>}
                          </span>
                        </span>
                      </div>
                      {/* 모바일 4행: 보조정보 + 컴팩트 액션버튼 */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                        <span style={{ fontSize: 10, color: '#999' }}>
                          {(() => {
                            const denom = b.total_rent + b.total_maintenance + b.total_cleaning - (b.discount || 0);
                            const fee = b.platform_fee || 0;
                            const pct = fee === 0 ? '0' : denom > 0 ? ((fee / denom) * 100).toFixed(1) : null;
                            return pct !== null ? <span style={{ color: '#94a3b8', marginRight: 4 }}>수수료 {pct}%</span> : null;
                          })()}
                          {formatNumber(dailyPrice)}원/일
                        </span>
                        <div style={{ display: 'flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => openMemoModal(b)} style={{ padding: '2px 6px', fontSize: 10, lineHeight: 1.2, fontWeight: 500, border: '1px solid #94a3b8', borderRadius: 3, background: '#f5f5f5', color: '#64748b', cursor: 'pointer' }}>메모</button>
                          {!isCancelled && (
                            <>
                              <button onClick={() => togglePaymentStatus(b.id)} style={{ padding: '2px 6px', fontSize: 10, lineHeight: 1.2, fontWeight: 500, border: `1px solid ${isPaid ? '#4caf50' : '#e53935'}`, borderRadius: 3, background: '#f5f5f5', color: isPaid ? '#4caf50' : '#e53935', cursor: 'pointer' }}>
                                {isPaid ? '입금완료' : '미입금'}
                              </button>
                              <button onClick={() => toggleDepositRefund(b.id)} style={{ padding: '2px 6px', fontSize: 10, lineHeight: 1.2, fontWeight: 500, border: `1px solid ${isRefunded ? '#4caf50' : '#e53935'}`, borderRadius: 3, background: '#f5f5f5', color: isRefunded ? '#4caf50' : '#e53935', cursor: 'pointer' }}>
                                {isRefunded ? '반환O' : '반환X'}
                              </button>
                              <button onClick={() => onEditBooking(b)} style={{ padding: '2px 6px', fontSize: 10, lineHeight: 1.2, fontWeight: 500, border: '1px solid #bbb', borderRadius: 3, background: '#f5f5f5', color: '#666', cursor: 'pointer' }}>수정</button>
                              <button onClick={() => onCancelBooking(b)} style={{ padding: '2px 6px', fontSize: 10, lineHeight: 1.2, fontWeight: 500, border: '1px solid #e53935', borderRadius: 3, background: '#fff', color: '#e53935', cursor: 'pointer' }}>취소</button>
                            </>
                          )}
                          {isCancelled && (
                            <button onClick={() => onCancelBooking(b)} style={{ padding: '2px 6px', fontSize: 10, lineHeight: 1.2, fontWeight: 500, border: '1px solid #e53935', borderRadius: 3, background: '#fff', color: '#e53935', cursor: 'pointer' }}>삭제</button>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* 데스크톱: 기존 레이아웃 유지 */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                          <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: 195, flexShrink: 0, fontWeight: 500 }}>
                            {b.check_in} ~ {b.check_out}
                          </span>
                          {b.booking_type === '직거래 연장' && (
                            <span onClick={(e) => showLabelTooltip(e, '직거래 연장')} style={{ display: 'inline-block', padding: '1px 6px', background: '#e8f5e9', color: '#2e7d32', border: '1px solid #2e7d32', borderRadius: 4, fontSize: 10, cursor: 'pointer' }}>직거래</span>
                          )}
                          <span
                            onClick={(e) => LABEL_SHORT[b.channel] ? showLabelTooltip(e, b.channel) : e.stopPropagation()}
                            style={{
                              display: 'inline-block', padding: '1px 6px', borderRadius: 4, fontSize: 10,
                              background: getChannelBg(b.channel),
                              color: channelBorderColors[b.channel] || '#1976d2',
                              border: `1px solid ${channelBorderColors[b.channel] || '#90caf9'}`,
                              cursor: LABEL_SHORT[b.channel] ? 'pointer' : 'default',
                            }}>
                            {shortLabel(b.channel)}
                          </span>
                          {!isCancelled && (
                            <>
                              <span style={{ fontSize: 9, color: '#1976d2', marginLeft: 4 }}>알림 발송</span>
                              {msgKeys.map((mk, mi) => {
                                const sent = !!ms[mk];
                                return (
                                  <span
                                    key={mk}
                                    onClick={(e) => { e.stopPropagation(); toggleMsgSent(b.id, mk); }}
                                    style={{
                                      fontSize: 9, padding: '1px 4px', borderRadius: 3, cursor: 'pointer',
                                      lineHeight: '1.3',
                                      border: `1px solid ${sent ? '#4caf50' : '#ccc'}`,
                                      color: sent ? '#4caf50' : '#bbb', background: 'transparent',
                                    }}
                                  >
                                    {sent ? '✓' : ''}{msgShort[mi]}
                                  </span>
                                );
                              })}
                            </>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{b.guest_name || ''} · {b.days}일</div>
                      </div>
                      <div style={{ textAlign: 'right', whiteSpace: 'nowrap', marginLeft: 12 }}>
                        {showCleanExp && <span style={{ fontSize: 11, color: '#e65100', marginRight: 8 }}>청소비 {formatNumber(b.cleaning_expense)}원</span>}
                        {(() => {
                          const denom = b.total_rent + b.total_maintenance + b.total_cleaning - (b.discount || 0);
                          const fee = b.platform_fee || 0;
                          const pct = fee === 0 ? '0' : denom > 0 ? ((fee / denom) * 100).toFixed(1) : null;
                          return pct !== null ? <span style={{ fontSize: 11, color: '#94a3b8', marginRight: 8 }}>수수료 {pct}%</span> : null;
                        })()}
                        <span style={{ fontSize: 11, color: '#888', marginRight: 8 }}>{formatNumber(dailyPrice)}원/일</span>
                        <span style={{ fontWeight: 600, color: '#1976d2' }}>{formatNumber(displayPrice)}원</span>
                        {b.negotiated_price > 0 && <span style={{ fontSize: 10, color: '#e65100', marginLeft: 4 }}>협의</span>}
                        {' '}
                        <button onClick={(e) => { e.stopPropagation(); openMemoModal(b); }} style={{ ...btnSm('#94a3b8', '#64748b'), marginRight: 4 }}>메모</button>
                        {!isCancelled && (
                          <>
                            <button onClick={(e) => { e.stopPropagation(); togglePaymentStatus(b.id); }} style={{ ...btnSm(isPaid ? '#4caf50' : '#e53935', isPaid ? '#4caf50' : '#e53935'), marginRight: 4 }}>
                              {isPaid ? '입금 완료' : '입금 확인'}
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); toggleDepositRefund(b.id); }} style={{ ...btnSm(isRefunded ? '#4caf50' : '#e53935', isRefunded ? '#4caf50' : '#e53935'), marginRight: 4 }}>
                              {isRefunded ? '보증금 반환O' : '보증금 반환X'}
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); onEditBooking(b); }} style={btnSm('#999', '#333')}>수정</button>
                            {' '}
                            <button onClick={(e) => { e.stopPropagation(); onCancelBooking(b); }} style={{ ...btnSm('#e53935', '#e53935'), background: '#fff' }}>취소</button>
                          </>
                        )}
                        {isCancelled && (
                          <>
                            <button onClick={(e) => { e.stopPropagation(); onCancelBooking(b); }} style={{ ...btnSm('#e53935', '#e53935'), background: '#fff' }}>삭제</button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {/* Expanded detail */}
                {expandedIds.has(b.id) && (
                  <div style={{ padding: '6px 12px 10px', marginTop: -2, marginBottom: 4, background: '#fafafa', borderRadius: '0 0 6px 6px', fontSize: 12, color: '#555' }}>
                    {b.guest_phone && `연락처: ${b.guest_phone} · `}
                    {b.channel && `채널: ${b.channel} · `}
                    {b.negotiated_price > 0 && `협의금액: ${formatNumber(b.negotiated_price)}원 · `}
                    {b.total_price > 0 && `자동계산: ${formatNumber(b.total_price)}원 · `}
                    {b.created_at && `등록: ${b.created_at.substring(0, 10)}`}
                  </div>
                )}
              </div>
            );
          })}
          {totalBkPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, marginTop: 8 }}>
              {Array.from({ length: totalBkPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setBookingPage(i + 1)}
                  style={{
                    minWidth: 28, height: 28,
                    border: '1px solid #ddd', borderRadius: 4, fontSize: 12, cursor: 'pointer',
                    background: bookingPage === i + 1 ? '#1976d2' : '#fff',
                    color: bookingPage === i + 1 ? '#fff' : '#333',
                    borderColor: bookingPage === i + 1 ? '#1976d2' : '#ddd',
                  }}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {hasBlocked && (
        <>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: '16px 0 8px' }}>
            예약불가 <span style={{ fontSize: 12, color: '#888', fontWeight: 400 }}>{allBlocked.length}건</span>
          </h3>
          {blSlice.map((bl, i) => {
            const idx = blStart + i;
            return (
              <div key={bl.id || idx}>
                <div
                  onClick={() => toggleExpand(`bl_${idx}`)}
                  style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    padding: isMobile ? '10px 12px' : '8px 12px',
                    gap: isMobile ? 8 : 0,
                    background: '#f0f0f0',
                    borderLeft: '3px solid #999',
                    borderRadius: 6,
                    fontSize: 13,
                    cursor: 'pointer',
                    marginBottom: 4,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500 }}>{bl.start} ~ {bl.end}</div>
                    <div style={{ fontSize: 12, color: '#999' }}>{bl.reason || '사유 없음'}</div>
                  </div>
                  <div style={{ textAlign: isMobile ? 'left' : 'right', whiteSpace: 'nowrap', marginLeft: isMobile ? 0 : 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11, color: '#999', marginRight: isMobile ? 0 : 8 }}>예약불가</span>
                    <button onClick={(e) => { e.stopPropagation(); onEditBlock(idx); }} style={isMobile ? { padding: '2px 6px', fontSize: 10, lineHeight: 1.2, fontWeight: 500, border: '1px solid #666', borderRadius: 3, background: '#f5f5f5', color: '#666', cursor: 'pointer' } : { ...btnSm('#666', '#666') }}>수정</button>
                    <button onClick={(e) => { e.stopPropagation(); onDeleteBlock(idx); }} style={isMobile ? { padding: '2px 6px', fontSize: 10, lineHeight: 1.2, fontWeight: 500, border: '1px solid #e53935', borderRadius: 3, background: '#fff', color: '#e53935', cursor: 'pointer' } : { ...btnSm('#e53935', '#e53935'), background: '#fff' }}>삭제</button>
                  </div>
                </div>
                {expandedIds.has(`bl_${idx}`) && (
                  <div style={{ padding: '6px 12px 10px', marginTop: -2, marginBottom: 4, background: '#fafafa', borderRadius: '0 0 6px 6px', fontSize: 12, color: '#555' }}>
                    사유: {bl.reason || '미입력'} · 기간: {bl.start} ~ {bl.end}
                  </div>
                )}
              </div>
            );
          })}
          {totalBlPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, marginTop: 8 }}>
              {Array.from({ length: totalBlPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setBlockedPage(i + 1)}
                  style={{
                    minWidth: 28, height: 28,
                    border: '1px solid #ddd', borderRadius: 4, fontSize: 12, cursor: 'pointer',
                    background: blockedPage === i + 1 ? '#1976d2' : '#fff',
                    color: blockedPage === i + 1 ? '#fff' : '#333',
                    borderColor: blockedPage === i + 1 ? '#1976d2' : '#ddd',
                  }}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
